import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }
  return document.documentElement.dataset.mapsKey || "";
}

function isLat(v) {
  return typeof v === "number" && isFinite(v) && v >= -90 && v <= 90;
}

function isLng(v) {
  return typeof v === "number" && isFinite(v) && v >= -180 && v <= 180;
}

function prop(p, k) {
  return p && typeof p[k] === "string" && p[k].trim() !== "" ? p[k].trim() : null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.autocompleteRequest = null;
    this.reverseRequest = null;
  }

  cancelAutocomplete() {
    if (this.autocompleteRequest != null) {
      this.autocompleteRequest.source.cancel();
      this.autocompleteRequest = null;
    }
  }

  cancelReverse() {
    if (this.reverseRequest != null) {
      this.reverseRequest.source.cancel();
      this.reverseRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.label) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Legacy-Fallback für Treffer ohne bekannten properties.type
  _legacyFields(p, g) {
    const name = prop(p, "name");
    const street = prop(p, "street") || (p.osm_key == "highway" ? name : null);
    const placeName = p.osm_key == "highway" ? null : name;

    if (street) {
      g.street = street;
      g.number = street === prop(p, "street") ? prop(p, "housenumber") : null;
    }

    g.zip = prop(p, "postcode");
    g.city = prop(p, "city");

    if (g.city && placeName && placeName !== g.city) {
      if (p.osm_key == "boundary" || (p.osm_key == "place" && p.osm_value == "suburb")) {
        g.district = placeName;
      } else {
        g.label = placeName;
      }
    } else if (!g.city && placeName) {
      g.label = placeName;
    }
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    // Koordinaten kommen als [lng, lat]
    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!isLat(lat) || !isLng(lng)) {
      return null;
    }

    const p = l.properties;
    const name = prop(p, "name");
    const g = {
      street: null,
      number: null,
      zip: prop(p, "postcode"),
      city: prop(p, "city"),
      district: prop(p, "district"),
      label: null,
    };

    switch (p.type) {
      case "house":
        g.street = prop(p, "street");
        g.number = g.street ? prop(p, "housenumber") : null;
        if (name && name !== g.street) {
          g.label = name;
        }
        break;

      case "street":
        g.street = prop(p, "street") || name;
        break;

      case "city":
        g.city = g.city || name;
        g.district = null;
        break;

      case "district":
        g.district = g.district || name;
        if (!g.city) {
          g.city = g.district;
          g.district = null;
        }
        break;

      case "locality":
        if (name && name !== g.city && name !== g.district) {
          g.label = name;
        }
        break;

      default:
        g.district = null;
        this._legacyFields(p, g);
    }

    if (g.district && g.district === g.city) {
      g.district = null;
    }

    // Ohne Orts- oder Straßenangabe bleibt der Name Beschriftung, nicht Stadt
    if (!g.city && !g.street && !g.label && name) {
      g.label = name;
    }

    const geoaddress = {};
    ["street", "number", "zip", "city", "district"].forEach(k => {
      if (g[k]) {
        geoaddress[k] = g[k];
      }
    });

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;
    geoaddress["state"] = prop(p, "state");
    geoaddress["country"] = prop(p, "country");
    geoaddress["name"] = name;
    geoaddress["type"] = p.type || null;

    geoaddress["text"] = this._geoaddressText(Object.assign({ label: g.label }, geoaddress));

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  // Liefert null, wenn die Anfrage abgebrochen wurde oder kein Key vorliegt.
  _request(slot, url, params) {
    const key = mapsKey();
    if (!key) {
      return Promise.resolve(null);
    }
    params.append("key", key);

    const request = { source: this.axios.CancelToken.source() };
    this[slot] = request;

    return this.axios.get(url, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (this[slot] !== request) {
        return null;
      }
      return res.data && Array.isArray(res.data.features) ? res.data.features : [];
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || this[slot] !== request) {
        return null;
      }
      throw thrown;
    }).finally(() => {
      if (this[slot] === request) {
        this[slot] = null;
      }
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    if (!query) {
      return Promise.resolve([]);
    }

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);

    return this._request("autocompleteRequest", SEARCH_URL, params).then(features => {
      if (features === null) {
        return null;
      }

      const results = [];
      features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    });
  }

  reverse(lat, lng) {
    this.cancelReverse();

    lat = Number(lat);
    lng = Number(lng);
    if (!isLat(lat) || !isLng(lng)) {
      return Promise.resolve(null);
    }

    const params = new URLSearchParams();
    params.append("lat", lat);
    params.append("lon", lng);
    params.append("lang", "de");

    return this._request("reverseRequest", REVERSE_URL, params).then(features => {
      if (features === null) {
        return null;
      }

      for (let i = 0; i < features.length; i++) {
        const geoaddress = this._geoaddress(features[i]);
        if (geoaddress) {
          return geoaddress;
        }
      }

      throw new Error("invalid data");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
